import type * as Phaser from "phaser"
import { AUDIO, TEX } from "../core/keys"
import { AUDIO_ASSETS } from "../data/audio"
import { getSectorBackground } from "../data/backgrounds"
import { HERO_DEFINITIONS } from "../data/characters"
import { ENEMY_DEFINITIONS } from "../data/enemies"
import {
  type AnimationSpec,
  COIN_ANIMATIONS,
  EXPLOSION_ANIMATIONS,
  HIT_ANIMATIONS,
  PROJECTILE_ANIMATIONS,
} from "../data/fx-animations"
import { ICON_ASSET_DEFINITIONS } from "../data/icon-registry"
import { MISC_ASSETS } from "../data/misc-assets"
import { SECTORS } from "../data/sectors"
import { toPublicAssetUrl } from "../utils"

const FIRST_SECTOR_ID = SECTORS[0]?.id ?? "occupied-skyline"

function queueAudio(scene: Phaser.Scene, keys: Set<string>): void {
  for (const audio of AUDIO_ASSETS) {
    if (keys.has(audio.key)) {
      if (scene.cache.audio.exists(audio.key)) continue
      scene.load.audio(audio.key, toPublicAssetUrl(audio.assetPath))
    }
  }
}

export function queueBootAssets(scene: Phaser.Scene): void {
  queueAudio(
    scene,
    new Set([AUDIO.SFX_UI_BEEP, AUDIO.SFX_UI_PRESS, AUDIO.SFX_UI_NAVIGATE]),
  )

  for (const hero of HERO_DEFINITIONS) {
    if (hero.animation) {
      scene.load.spritesheet(
        hero.textureKey,
        toPublicAssetUrl(hero.animation.assetPath),
        {
          frameWidth: hero.animation.frameWidth,
          frameHeight: hero.animation.frameHeight,
        },
      )
    } else {
      scene.load.image(hero.textureKey, toPublicAssetUrl(hero.assetPath))
    }
    scene.load.image(`portrait_${hero.id}`, toPublicAssetUrl(hero.portraitPath))
  }

  for (const asset of MISC_ASSETS) {
    scene.load.image(asset.textureKey, toPublicAssetUrl(asset.assetPath))
  }

  queueFxSpritesheets(scene)
}

export function queueWaveAssets(scene: Phaser.Scene, wave = 1): void {
  queueHeroAssets(scene)
  queueEnemyAssets(scene)
  queueFxSpritesheets(scene)
  queueBackgroundAssets(scene, getWaveSectorIds(wave))
  queueIconAssets(scene)

  const audioToQueue = new Set(AUDIO_ASSETS.map((audio) => audio.key))
  // Defer boss music until we are near a boss wave (first boss is wave 3)
  if (wave < 3) {
    audioToQueue.delete(AUDIO.MUSIC_BOSS)
  }
  queueAudio(scene, audioToQueue)
}

export function queueSectorBackgroundAssets(
  scene: Phaser.Scene,
  sectorId: string,
): boolean {
  let queued = false
  const background = getSectorBackground(sectorId)
  for (const layer of background.layers) {
    if (scene.textures.exists(layer.key)) continue
    scene.load.image(layer.key, toPublicAssetUrl(layer.assetPath))
    queued = true
  }
  return queued
}

function queueHeroAssets(scene: Phaser.Scene): void {
  for (const hero of HERO_DEFINITIONS) {
    if (hero.animation) {
      scene.load.spritesheet(
        hero.textureKey,
        toPublicAssetUrl(hero.animation.assetPath),
        {
          frameWidth: hero.animation.frameWidth,
          frameHeight: hero.animation.frameHeight,
        },
      )
    } else {
      scene.load.image(hero.textureKey, toPublicAssetUrl(hero.assetPath))
    }

    if (!scene.textures.exists(`portrait_${hero.id}`)) {
      scene.load.image(
        `portrait_${hero.id}`,
        toPublicAssetUrl(hero.portraitPath),
      )
    }
  }

  scene.load.image(
    TEX.PLAYER_SPRITE,
    toPublicAssetUrl(HERO_DEFINITIONS[0].assetPath),
  )
}

function queueEnemyAssets(scene: Phaser.Scene): void {
  for (const enemy of Object.values(ENEMY_DEFINITIONS)) {
    if (scene.textures.exists(enemy.texture)) continue
    const isSpriteSheet =
      enemy.assetPath.endsWith("sheet.webp") || !!enemy.animation
    if (isSpriteSheet && enemy.animation) {
      scene.load.spritesheet(enemy.texture, toPublicAssetUrl(enemy.assetPath), {
        frameWidth: enemy.animation.frameWidth,
        frameHeight: enemy.animation.frameHeight,
      })
    } else {
      scene.load.image(enemy.texture, toPublicAssetUrl(enemy.assetPath))
    }
  }

  if (!scene.textures.exists(TEX.BOSS_BRAIN_EXPOSED)) {
    scene.load.spritesheet(
      TEX.BOSS_BRAIN_EXPOSED,
      toPublicAssetUrl("enemies/boss-brain/exposed-idle.webp"),
      {
        frameWidth: 1024,
        frameHeight: 1024,
      },
    )
  }
  if (!scene.textures.exists(TEX.BOSS_SYNAPSE)) {
    scene.load.image(
      TEX.BOSS_SYNAPSE,
      toPublicAssetUrl("enemies/brain/synapse-node.webp"),
    )
  }
  if (!scene.textures.exists(TEX.ENEMY_MINE_DRONE_CLOSE)) {
    scene.load.image(
      TEX.ENEMY_MINE_DRONE_CLOSE,
      toPublicAssetUrl("enemies/mine-drone/mine-drone-close.webp"),
    )
  }
  if (!scene.textures.exists(TEX.ENEMY_MINE_DRONE_OPEN)) {
    scene.load.image(
      TEX.ENEMY_MINE_DRONE_OPEN,
      toPublicAssetUrl("enemies/mine-drone/mine-drone-open.webp"),
    )
  }

  const humanoidSheets: [string, string][] = [
    [TEX.BOSS_HUMANOID_JUMP, "enemies/boss-humanoid/jump-sheet.webp"],
    [TEX.BOSS_HUMANOID_DASH, "enemies/boss-humanoid/dash-sheet.webp"],
    [TEX.BOSS_HUMANOID_CHARGE, "enemies/boss-humanoid/charge-sheet.webp"],
    [TEX.BOSS_HUMANOID_FIRE, "enemies/boss-humanoid/fire-sheet.webp"],
  ]
  for (const [key, path] of humanoidSheets) {
    if (!scene.textures.exists(key)) {
      scene.load.spritesheet(key, toPublicAssetUrl(path), {
        frameWidth: 1024,
        frameHeight: 1024,
      })
    }
  }
}

function queueFxSpritesheets(scene: Phaser.Scene): void {
  const allSpecs: AnimationSpec[] = [
    ...Object.values(EXPLOSION_ANIMATIONS),
    ...Object.values(PROJECTILE_ANIMATIONS),
    ...Object.values(HIT_ANIMATIONS),
    ...Object.values(COIN_ANIMATIONS),
  ]

  for (const spec of allSpecs) {
    if (scene.textures.exists(spec.texture)) {
      if (scene.textures.getFrame(spec.texture, 0)) continue
      scene.textures.remove(spec.texture)
    }
    scene.load.spritesheet(spec.texture, toPublicAssetUrl(spec.assetPath), {
      frameWidth: spec.frameWidth,
      frameHeight: spec.frameHeight,
    })
  }
}

function queueBackgroundAssets(scene: Phaser.Scene, sectorIds: string[]): void {
  for (const sectorId of sectorIds) {
    queueSectorBackgroundAssets(scene, sectorId)
  }
}

function queueIconAssets(scene: Phaser.Scene): void {
  for (const icon of ICON_ASSET_DEFINITIONS) {
    if (!scene.textures.exists(icon.textureKey)) {
      scene.load.image(icon.textureKey, toPublicAssetUrl(icon.assetPath))
    }
  }
}

function getWaveSectorIds(wave: number): string[] {
  const current =
    SECTORS.find(
      (sector) => wave >= sector.waveStart && wave <= sector.waveEnd,
    ) ?? SECTORS[0]
  const currentIndex = Math.max(0, SECTORS.indexOf(current))
  const next = SECTORS[currentIndex + 1]
  return [current?.id ?? FIRST_SECTOR_ID, next?.id].filter(Boolean) as string[]
}

export function createLoadedAnimations(scene: Phaser.Scene): void {
  createHeroAnimations(scene)
  createEnemyAnimations(scene)
  createHumanoidBossAnimations(scene)
  createFxAnimations(scene)
  createCoinAnimations(scene)
}

function createEnemyAnimations(scene: Phaser.Scene): void {
  for (const enemy of Object.values(ENEMY_DEFINITIONS)) {
    if (!enemy.animation) continue
    if (!scene.textures.exists(enemy.texture)) continue
    if (scene.anims.exists(enemy.animation.key))
      scene.anims.remove(enemy.animation.key)

    scene.anims.create({
      key: enemy.animation.key,
      frames: scene.anims.generateFrameNumbers(enemy.texture, {
        start: 0,
        end: enemy.animation.frames - 1,
      }),
      frameRate: enemy.animation.frameRate,
      repeat: enemy.animation.repeat ?? 0,
    })
  }

  // Exposed Brain Phase Animation
  if (
    scene.textures.exists(TEX.BOSS_BRAIN_EXPOSED) &&
    !scene.anims.exists("anim_brain_exposed")
  ) {
    const frames = scene.anims.generateFrameNumbers(TEX.BOSS_BRAIN_EXPOSED, {
      start: 0,
      end: 3, // 2x2 = 4 frames
    })

    if (frames && frames.length > 0) {
      scene.anims.create({
        key: "anim_brain_exposed",
        frames,
        frameRate: 8,
        repeat: -1,
      })
    }
  }
}

function createHumanoidBossAnimations(scene: Phaser.Scene): void {
  const specs: Array<{
    key: string
    tex: string
    frames: number
    frameRate: number
  }> = [
    {
      key: "anim_boss_humanoid_jump",
      tex: TEX.BOSS_HUMANOID_JUMP,
      frames: 8,
      frameRate: 8,
    },
    {
      key: "anim_boss_humanoid_dash",
      tex: TEX.BOSS_HUMANOID_DASH,
      frames: 4,
      frameRate: 10,
    },
    {
      key: "anim_boss_humanoid_charge",
      tex: TEX.BOSS_HUMANOID_CHARGE,
      frames: 8,
      frameRate: 8,
    },
    {
      key: "anim_boss_humanoid_fire",
      tex: TEX.BOSS_HUMANOID_FIRE,
      frames: 4,
      frameRate: 12,
    },
  ]

  for (const spec of specs) {
    if (!scene.textures.exists(spec.tex)) continue
    if (scene.anims.exists(spec.key)) scene.anims.remove(spec.key)
    scene.anims.create({
      key: spec.key,
      frames: scene.anims.generateFrameNumbers(spec.tex, {
        start: 0,
        end: spec.frames - 1,
      }),
      frameRate: spec.frameRate,
      repeat: -1,
    })
  }
}

function createHeroAnimations(scene: Phaser.Scene): void {
  for (const hero of HERO_DEFINITIONS) {
    const animation = hero.animation
    if (!animation) continue
    if (!scene.textures.exists(hero.textureKey)) continue
    if (scene.anims.exists(animation.key)) scene.anims.remove(animation.key)

    scene.anims.create({
      key: animation.key,
      frames: scene.anims.generateFrameNumbers(hero.textureKey, {
        start: 0,
        end: animation.frames - 1,
      }),
      frameRate: animation.frameRate,
      repeat: animation.repeat ?? -1,
    })
  }
}

function createFxAnimations(scene: Phaser.Scene): void {
  const allSpecs: AnimationSpec[] = [
    ...Object.values(EXPLOSION_ANIMATIONS),
    ...Object.values(PROJECTILE_ANIMATIONS),
    ...Object.values(HIT_ANIMATIONS),
  ]

  for (const cfg of allSpecs) {
    if (!scene.textures.exists(cfg.texture) || scene.anims.exists(cfg.key))
      continue

    scene.anims.create({
      key: cfg.key,
      frames: scene.anims.generateFrameNumbers(cfg.texture, {
        start: 0,
        end: cfg.frames - 1,
      }),
      frameRate: cfg.frameRate,
      repeat: cfg.repeat ?? 0,
    })
  }
}

function createCoinAnimations(scene: Phaser.Scene): void {
  for (const cfg of Object.values(COIN_ANIMATIONS)) {
    if (scene.anims.exists(cfg.key) || !scene.textures.exists(cfg.texture))
      continue
    const frames = scene.anims.generateFrameNumbers(cfg.texture, {
      start: 0,
      end: cfg.frames - 1,
    })
    if (frames.length === 0) continue

    scene.anims.create({
      key: cfg.key,
      frames,
      frameRate: cfg.frameRate,
      repeat: cfg.repeat ?? 0,
    })
  }
}
